import { fetchWithCSRF } from "@/helpers/common/csrf";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

interface ShieldMatePlayerSummary {
    id: string;
    player_name: string;
    clan_tag: string;
    clan_color: string | null;
    shield_mates_count: number;
}

const API_URL = import.meta.env.VITE_VANDERHEIM_NINJA_API_URL;

interface Pagination {
    page: number;
    page_size: number;
    total_items: number;
    total_pages: number;
    has_next_page: boolean;
    has_previous_page: boolean;
}

export default function ShieldMatesPage() {
    const [entries, setEntries] = useState<ShieldMatePlayerSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [pagination, setPagination] = useState<Pagination>({
        page: 1,
        page_size: 25,
        total_items: 0,
        total_pages: 1,
        has_next_page: false,
        has_previous_page: false,
    });

    const fetchEntries = useCallback(
        async (page = 1) => {
            setLoading(true);
            try {
                const params = new URLSearchParams({
                    page: String(page),
                    page_size: "25",
                    sort_by: "shield_mates_count",
                    order: "desc",
                });
                if (searchQuery) {
                    params.set("search", searchQuery);
                }

                const res = await fetchWithCSRF(
                    `${API_URL}/longhouse-api/village/shield-mates/?${params}`,
                );
                if (res.ok) {
                    const data = await res.json();
                    setEntries(data.results);
                    setPagination(data.pagination);
                }
            } catch (error) {
                console.error("Error fetching shield mates:", error);
            } finally {
                setLoading(false);
            }
        },
        [searchQuery],
    );

    useEffect(() => {
        fetchEntries(1);
    }, [fetchEntries]);

    return (
        <div className="mx-auto mt-4 w-full px-4">
            <div className="mb-6 rounded-2xl border border-slate-300 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
                    Shield Mates
                </h1>
                <p className="mt-2 text-slate-600 dark:text-slate-400">
                    View players with shield mates, then drill in to manage
                    individual shield mate relationships.
                </p>
            </div>

            <div className="mb-6 flex flex-wrap items-center gap-4">
                <div className="relative">
                    <Search className="absolute top-1/2 left-3 h-5 w-5 -translate-y-1/2 text-slate-400" />
                    <input
                        type="text"
                        placeholder="Search by player name..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="rounded-lg border border-slate-300 bg-white py-2.5 pr-4 pl-10 text-sm text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:placeholder-slate-500"
                    />
                </div>
                <span className="text-sm text-slate-500 dark:text-slate-400">
                    {pagination.total_items} total players
                </span>
            </div>

            <div className="rounded-2xl border border-slate-300 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-900">
                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="border-brand h-8 w-8 animate-spin rounded-full border-4 border-solid border-r-transparent" />
                    </div>
                ) : entries.length === 0 ? (
                    <div className="p-8 text-center">
                        <p className="text-slate-600 dark:text-slate-400">
                            No shield mate players found
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="border-b border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                            Player
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                            Clan
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                            Shield Mates
                                        </th>
                                        <th className="px-6 py-3 text-right text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                                    {entries.map((entry) => (
                                        <tr
                                            key={entry.id}
                                            className="transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50"
                                        >
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <Link
                                                    to={`/longhouse/shield-mates/${entry.id}`}
                                                    className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline dark:text-blue-400 dark:hover:text-blue-300"
                                                >
                                                    {entry.player_name}
                                                </Link>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span
                                                    className="rounded px-2 py-1 text-xs font-semibold text-white"
                                                    style={{
                                                        backgroundColor:
                                                            entry.clan_color ||
                                                            "#475569",
                                                    }}
                                                >
                                                    {entry.clan_tag}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-sm font-semibold whitespace-nowrap text-slate-900 dark:text-slate-100">
                                                {entry.shield_mates_count}
                                            </td>
                                            <td className="px-6 py-4 text-right whitespace-nowrap">
                                                <Link
                                                    to={`/longhouse/shield-mates/${entry.id}`}
                                                    className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
                                                >
                                                    View
                                                </Link>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="flex items-center justify-between border-t border-slate-200 px-6 py-4 dark:border-slate-700">
                            <span className="text-sm text-slate-600 dark:text-slate-400">
                                Page {pagination.page} of{" "}
                                {pagination.total_pages}
                            </span>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() =>
                                        fetchEntries(pagination.page - 1)
                                    }
                                    disabled={!pagination.has_previous_page}
                                    className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                </button>
                                <button
                                    onClick={() =>
                                        fetchEntries(pagination.page + 1)
                                    }
                                    disabled={!pagination.has_next_page}
                                    className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
                                >
                                    <ChevronRight className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
